/**
 * Who is signed in, and which organization the dashboard is acting as.
 *
 * The active organization is deliberately not stored on the server session. The
 * dashboard is used in several tabs, and a server-side "current org" would mean
 * switching in one tab silently changed what another tab displayed — and what
 * its next write applied to. The server takes the organization as a header on
 * every request; this holds the value that goes in it.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { api, setActiveOrg, UnauthenticatedError } from "./api/client";
import type { Capability, Membership, Role, SessionState } from "./api/types";

type Phase = "loading" | "anonymous" | "ready";

interface SessionContextValue {
  phase: Phase;
  session: SessionState | null;
  /** Null when the account belongs to no organization yet. */
  orgId: string | null;
  /** The membership orgId names, with its role and any pending deletion. */
  org: Membership | null;
  role: Role | null;

  /**
   * Whether the active role may do something, by the server's own answer.
   *
   * Use it to decide what to *draw* — a control nobody can use is worse than no
   * control, because it fails only after being clicked. It is not a permission
   * check: the request is checked again on the server, which is the side that
   * an attacker does not control.
   */
  can: (capability: Capability) => boolean;

  switchOrg: (id: string) => void;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * `canWrite(role)` and `isAdmin(role)` used to live here and are deliberately
 * gone.
 *
 * Every screen that called them now asks `can(capability)` instead. The two
 * helpers could only describe a ladder — `canWrite` returned true for a
 * developer, who cannot edit a contact, and `isAdmin` gated the API-keys screen
 * that a developer is precisely the person to use. Keeping them as a
 * convenience would mean the next screen reaches for the wrong one and draws
 * controls that 403 when clicked.
 *
 * The server's capability names are the vocabulary; `roles.ts` explains them to
 * whoever is picking a role.
 */

/**
 * Remembers the last organization per user, not globally: on a shared machine
 * the next person must not land in the previous one's organization, and their
 * own choice should survive a browser restart.
 */
const storageKey = (userId: string) => `opencomms.org.${userId}`;

function rememberedOrg(userId: string): string | null {
  try {
    return window.localStorage.getItem(storageKey(userId));
  } catch {
    // Private browsing modes throw on access. A forgotten preference is a much
    // smaller problem than a dashboard that will not start.
    return null;
  }
}

function rememberOrg(userId: string, orgId: string): void {
  try {
    window.localStorage.setItem(storageKey(userId), orgId);
  } catch {
    /* ignored, as above */
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [session, setSession] = useState<SessionState | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const found = await api.get<SessionState>("/auth/session", { unscoped: true });

      // A remembered organization is only usable if the user is still a member;
      // access can be revoked between visits.
      //
      // A remembered organization that is now scheduled for deletion is still
      // selected, because its owner arriving to find it gone with no
      // explanation is worse than landing on the screen that says so and
      // offers the undo. What must not happen is *falling back* to one: a
      // first load would otherwise open in a deleted organization and 401 on
      // every request, for someone who never asked to look at it.
      const remembered = rememberedOrg(found.user.id);
      const usable = found.organizations.find((o) => o.id === remembered)
        ? remembered
        : (found.organizations.find((o) => !o.purge_after)?.id ??
          found.organizations[0]?.id ??
          null);

      // Set on the client before anything renders, so no screen can fire a /v1
      // request without the header.
      setActiveOrg(usable);
      setSession(found);
      setOrgId(usable);
      setPhase("ready");
    } catch (err) {
      if (err instanceof UnauthenticatedError) {
        setActiveOrg(null);
        setSession(null);
        setOrgId(null);
        setPhase("anonymous");
        return;
      }
      throw err;
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const switchOrg = useCallback(
    (id: string) => {
      if (!session) return;
      setActiveOrg(id);
      setOrgId(id);
      rememberOrg(session.user.id, id);
    },
    [session],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout", undefined, { unscoped: true });
    } finally {
      // Whatever the server said, this browser is done with the session.
      setActiveOrg(null);
      setSession(null);
      setOrgId(null);
      setPhase("anonymous");
    }
  }, []);

  const value = useMemo<SessionContextValue>(() => {
    const org = session?.organizations.find((o) => o.id === orgId) ?? null;

    // A Set rather than Array.includes on every render: nav alone asks a dozen
    // times per paint, and the membership object is stable between loads.
    const granted = new Set(org?.capabilities ?? []);

    return {
      phase,
      session,
      orgId,
      org,
      role: org?.role ?? null,
      can: (capability: Capability) => granted.has(capability),
      switchOrg,
      logout,
      reload: load,
    };
  }, [phase, session, orgId, switchOrg, logout, load]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside a SessionProvider");
  return value;
}
